/** Manage → Team (members and invitations). */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../meta';
import { DangerZone, ErrorBox, Panel, fmtDate } from '../components';
import { t, tp } from '../i18n';
import { usePublicConfig } from './Auth';

interface TeamDetails {
  id: number;
  name: string;
  project_count: number;
  member_count: number;
}

interface TeamData {
  team: TeamDetails;
  is_admin: boolean;
  members: { id: number; username: string; realname: string; email: string; enabled: boolean; last_visit: string | null; access_level: number; joined_at: string; must_change_password: boolean }[];
  invites: { id: number; email: string; access_level: number; created_at: string; expires_at: string; invited_by: string | null }[];
}

export function TeamTab() {
  const mantis = usePublicConfig().data?.mantis_database === true;
  const { meta, items, label } = useMeta();
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: ['team'], queryFn: () => api.get<TeamData>('/api/team') });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['team'] });
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['meta'] });
  };
  const [name, setName] = useState<string | null>(null);
  const [invite, setInvite] = useState({ email: '', access_level: 55 });
  const [lastLink, setLastLink] = useState<{ email: string; link: string } | null>(null);
  const [withFiles, setWithFiles] = useState(false);
  const navigate = useNavigate();

  const rename = useMutation({ mutationFn: () => api.patch('/api/team', { name: name?.trim() }), onSuccess: () => { setName(null); refresh(); } });
  const setLevel = useMutation({ mutationFn: ({ id, level }: { id: number; level: number }) => api.patch(`/api/team/members/${id}`, { access_level: level }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: number) => api.del(`/api/team/members/${id}`), onSuccess: refresh });
  const sendInvite = useMutation({
    mutationFn: () => api.post<{ link: string }>('/api/team/invites', { email: invite.email.trim(), access_level: invite.access_level }),
    onSuccess: (r) => {
      setLastLink({ email: invite.email.trim(), link: r.link });
      setInvite({ ...invite, email: '' });
      refresh();
    },
  });
  const revoke = useMutation({ mutationFn: (id: number) => api.del(`/api/team/invites/${id}`), onSuccess: refresh });

  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">{t('Loading…')}</p>;
  const admin = data.is_admin;

  return (
    <>
      <Panel
        title={
          name == null ? (
            data.team.name
          ) : (
            t('Rename team')
          )
        }
        actions={
          admin &&
          (name == null ? (
            <button onClick={() => setName(data.team.name)}>{t('Rename')}</button>
          ) : (
            <form
              className="inline-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim()) rename.mutate();
              }}
            >
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
              <button className="primary">{t('Save')}</button>
              <button type="button" onClick={() => setName(null)}>
                {t('Cancel')}
              </button>
            </form>
          ))
        }
      >
        <ErrorBox error={rename.error || setLevel.error || remove.error} />
        <table className="simple">
          <thead>
            <tr>
              <th>{t('Username')}</th>
              <th>{t('Real Name')}</th>
              {admin && <th>{t('E-mail')}</th>}
              <th>{t('Access Level')}</th>
              <th>{t('Joined')}</th>
              <th>{t('Last Visit')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.id} className={m.enabled ? '' : 'disabled'}>
                <td>
                  {m.username}
                  {m.id === meta.user.id && <span className="pill">{t('you')}</span>}
                  {m.must_change_password && <span className="pill" title={t('Has not replaced the temporary password yet')}>{t('temporary password')}</span>}
                </td>
                <td>{m.realname}</td>
                {admin && <td>{m.email}</td>}
                <td>
                  {admin ? (
                    <select value={m.access_level} onChange={(e) => setLevel.mutate({ id: m.id, level: Number(e.target.value) })}>
                      {items('access_level').map((a) => (
                        <option key={a.value} value={a.value}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    label('access_level', m.access_level)
                  )}
                </td>
                <td className="nowrap">{fmtDate(m.joined_at, false)}</td>
                <td className="nowrap">{fmtDate(m.last_visit)}</td>
                <td>
                  {!mantis && (admin || m.id === meta.user.id) && (
                    <button
                      className="link danger"
                      onClick={() => {
                        const msg = m.id === meta.user.id ? t('Leave "{team}"?', { team: data.team.name }) : t('Remove {user} from the team?', { user: m.username });
                        if (confirm(msg)) remove.mutate(m.id);
                      }}
                    >
                      {m.id === meta.user.id ? t('Leave') : t('Remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small">
          {t("The access level applies to every project of the team; a project's members list can raise or lower it for that project. Administrators manage the team and its projects.")}
        </p>
      </Panel>

      {admin && (
        <Panel title={t('Invite people')}>
          <form
            className="inline-form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (invite.email.trim()) sendInvite.mutate();
            }}
          >
            <input type="email" placeholder={t('name@company.com')} value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <select value={invite.access_level} onChange={(e) => setInvite({ ...invite, access_level: Number(e.target.value) })}>
              {items('access_level').map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
            <button className="primary" disabled={sendInvite.isPending || !invite.email.trim()}>
              {t('Send invitation')}
            </button>
          </form>
          <ErrorBox error={sendInvite.error || revoke.error} />
          {lastLink && (
            <div className="banner success">
              {t("Invitation sent to {email}. If the e-mail doesn't arrive, send them this link:", { email: lastLink.email })} <CopyLink text={lastLink.link} />
            </div>
          )}
          {data.invites.length > 0 && (
            <table className="simple">
              <thead>
                <tr>
                  <th>{t('Pending invitation')}</th>
                  <th>{t('Access Level')}</th>
                  <th>{t('Invited by')}</th>
                  <th>{t('Expires')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.invites.map((i) => (
                  <tr key={i.id}>
                    <td>{i.email}</td>
                    <td>{label('access_level', i.access_level)}</td>
                    <td>{i.invited_by}</td>
                    <td className="nowrap">{fmtDate(i.expires_at, false)}</td>
                    <td>
                      <button className="link danger" onClick={() => revoke.mutate(i.id)}>
                        {t('Revoke')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {admin && (
        <Panel title={t('Export team data')}>
          <p>
            {t('Downloads everything of the team as a .tar.gz archive: members, projects, issues with notes, history and relationships (JSON), and optionally the attachment files. Windows, macOS and 7-Zip open it.')}
          </p>
          <div className="export-options">
            <label className="check">
              <input type="checkbox" checked={withFiles} onChange={(e) => setWithFiles(e.target.checked)} /> {t('Include attachment files (can be large)')}
            </label>
            <a className="btn primary" href={`/api/team/export${withFiles ? '?files=1' : ''}`} download>
              {t('Download export')}
            </a>
          </div>
        </Panel>
      )}

      {admin && !mantis && (
        <DangerZone
          title={t('Delete team')}
          button={t('Delete team')}
          confirmText={data.team.name}
          needPassword
          onConfirm={async (password) => {
            await api.post('/api/team/delete', { name: data.team.name, password });
            await qc.invalidateQueries();
            navigate('/teams');
          }}
        >
          <p>
            {tp(
              data.team.project_count,
              'Deletes the team with its {n} project, all issues and files, API keys and webhooks. Members lose access; their accounts stay. This cannot be undone, so export the data first if you may need it.',
              'Deletes the team with its {n} projects, all issues and files, API keys and webhooks. Members lose access; their accounts stay. This cannot be undone, so export the data first if you may need it.',
            )}
          </p>
        </DangerZone>
      )}
    </>
  );
}

export function CopyLink({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="copy-link">
      <code>{text}</code>
      <button
        type="button"
        className="link"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
          } catch {
            /* clipboard blocked: the link is still visible */
          }
        }}
      >
        {copied ? t('Copied') : t('Copy')}
      </button>
    </span>
  );
}
